/**
 * Timeline Yjs Sync Hook
 *
 * Persists timeline state to Yjs document with debouncing.
 * Allows timeline edits to be saved and restored across sessions.
 *
 * Uses a Yjs map structure:
 * - timeline_drafts/{nodeId} -> TimelineState JSON
 *
 * Persists speed and trackIndex fields. Excludes ephemeral clipboard
 * fields (copiedClip, copiedClipType) from Yjs persistence.
 */

import { useEffect, useRef } from 'react'
import * as Y from 'yjs'
import type { TimelineState, TimelineClip, TimelineAudioClip } from './types'

interface UseTimelineYjsSyncOptions {
  /** The Yjs document (from useYDoc) */
  ydoc: Y.Doc | null

  /** Whether Yjs is synced */
  synced: boolean

  /** Current timeline state */
  timelineState: TimelineState

  /** Node ID (unique identifier for this timeline) */
  nodeId: string

  /** Callback to restore state from Yjs */
  onRestoreState: (state: TimelineState) => void

  /** Debounce delay in milliseconds (default: 2000) */
  debounceMs?: number

  /** Set of valid video source node IDs (edge-connected). Clips not in this set are pruned on restore. */
  validVideoSourceIds?: Set<string>

  /** Set of valid audio source node IDs (edge-connected). Audio clips not in this set are pruned on restore. */
  validAudioSourceIds?: Set<string>
}

/**
 * Sync timeline state to Yjs with debouncing
 */
export function useTimelineYjsSync({
  ydoc,
  synced,
  timelineState,
  nodeId,
  onRestoreState,
  debounceMs = 2000,
  validVideoSourceIds,
  validAudioSourceIds,
}: UseTimelineYjsSyncOptions) {
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const hasRestoredRef = useRef(false)
  const lastSavedStateRef = useRef<string>('')

  // Restore state from Yjs on mount
  useEffect(() => {
    if (!ydoc || !synced || hasRestoredRef.current) {
      return
    }

    const timelineDrafts = ydoc.getMap<Y.Map<unknown>>('timeline_drafts')
    const draftData = timelineDrafts.get(nodeId)

    if (draftData) {
      try {
        // Convert Yjs Map to plain object
        const draftObj = draftData instanceof Y.Map
          ? Object.fromEntries(draftData.entries())
          : draftData

        // Parse into TimelineState
        let restoredState = parseTimelineState(draftObj)

        if (restoredState) {
          // Filter restored clips to only include those whose sourceNodeId
          // is still edge-connected. This prevents stale clips from flashing
          // on open when Yjs restoration races with edge-based initialization.
          if (validVideoSourceIds && validVideoSourceIds.size > 0) {
            restoredState = {
              ...restoredState,
              clips: restoredState.clips.filter(c => validVideoSourceIds.has(c.sourceNodeId)),
            }
          }
          if (validAudioSourceIds && validAudioSourceIds.size > 0) {
            restoredState = {
              ...restoredState,
              audioClips: restoredState.audioClips.filter(c => validAudioSourceIds.has(c.sourceNodeId)),
            }
          }
          // Recompute totalDuration after filtering
          restoredState = {
            ...restoredState,
            totalDuration: restoredState.clips.reduce((sum, c) => sum + c.effectiveDuration, 0),
          }

          console.log(`[Timeline Yjs] Restored state for node ${nodeId}`, restoredState)
          onRestoreState(restoredState)
          lastSavedStateRef.current = JSON.stringify(restoredState)
        }
      } catch (error) {
        console.error('[Timeline Yjs] Failed to restore state:', error)
      }
    }

    hasRestoredRef.current = true
  }, [ydoc, synced, nodeId, onRestoreState, validVideoSourceIds, validAudioSourceIds])

  // Debounced save to Yjs
  useEffect(() => {
    if (!ydoc || !synced || !hasRestoredRef.current) {
      return
    }

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      // Exclude ephemeral clipboard fields from serialization
      const persistableState = {
        clips: timelineState.clips,
        audioClips: timelineState.audioClips,
        playheadPosition: timelineState.playheadPosition,
        zoomLevel: timelineState.zoomLevel,
        isPlaying: timelineState.isPlaying,
        selectedClipId: timelineState.selectedClipId,
        selectedClipType: timelineState.selectedClipType,
        totalDuration: timelineState.totalDuration,
      }
      const currentStateStr = JSON.stringify(persistableState)

      // Skip if state hasn't changed
      if (currentStateStr === lastSavedStateRef.current) {
        return
      }

      try {
        const timelineDrafts = ydoc.getMap<Y.Map<unknown>>('timeline_drafts')

        // Create a new Yjs Map for this draft
        const draftMap = new Y.Map()

        // Store timeline state fields (including speed and trackIndex via clips)
        draftMap.set('clips', timelineState.clips)
        draftMap.set('audioClips', timelineState.audioClips)
        draftMap.set('playheadPosition', timelineState.playheadPosition)
        draftMap.set('zoomLevel', timelineState.zoomLevel)
        draftMap.set('isPlaying', timelineState.isPlaying)
        draftMap.set('selectedClipId', timelineState.selectedClipId)
        draftMap.set('selectedClipType', timelineState.selectedClipType)
        draftMap.set('totalDuration', timelineState.totalDuration)
        // Note: copiedClip and copiedClipType are NOT persisted (ephemeral)

        // Save to Yjs
        timelineDrafts.set(nodeId, draftMap)

        lastSavedStateRef.current = currentStateStr
        console.log(`[Timeline Yjs] Saved state for node ${nodeId}`)
      } catch (error) {
        console.error('[Timeline Yjs] Failed to save state:', error)
      }
    }, debounceMs)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [ydoc, synced, timelineState, nodeId, debounceMs])
}

/**
 * Parse Yjs data into TimelineState with defaults for new V2 fields
 */
function parseTimelineState(data: unknown): TimelineState | null {
  if (!data || typeof data !== 'object') {
    return null
  }

  const obj = data as Record<string, unknown>

  // Add defaults for speed/trackIndex on clips loaded from Yjs
  const rawClips = Array.isArray(obj.clips) ? obj.clips : []
  const clips = rawClips.map((clip: Record<string, unknown>) => ({
    ...clip,
    speed: typeof clip.speed === 'number' ? clip.speed : 1.0,
  })) as TimelineClip[]

  const rawAudioClips = Array.isArray(obj.audioClips) ? obj.audioClips : []
  const audioClips = rawAudioClips.map((clip: Record<string, unknown>) => ({
    ...clip,
    speed: typeof clip.speed === 'number' ? clip.speed : 1.0,
    trackIndex: typeof clip.trackIndex === 'number' ? clip.trackIndex : 0,
  })) as TimelineAudioClip[]

  return {
    clips,
    audioClips,
    playheadPosition: typeof obj.playheadPosition === 'number' ? obj.playheadPosition : 0,
    zoomLevel: typeof obj.zoomLevel === 'number' ? obj.zoomLevel : 50,
    isPlaying: typeof obj.isPlaying === 'boolean' ? obj.isPlaying : false,
    selectedClipId: typeof obj.selectedClipId === 'string' ? obj.selectedClipId : null,
    selectedClipType: (obj.selectedClipType === 'video' || obj.selectedClipType === 'audio') ? obj.selectedClipType : null,
    totalDuration: typeof obj.totalDuration === 'number' ? obj.totalDuration : 0,
    // Ephemeral fields - always start fresh
    copiedClip: null,
    copiedClipType: null,
  }
}
